import { isEmpty } from '@sgme/fp';
import type {
  SecondaryOrderStatePath,
  TradeAuthorization,
  TradeWay,
} from 'state/secondaryMarket/secondaryMarket.model';
import type { ReferentialData } from 'state/secondaryMarket/shared/referential/referential.model';
import type { AppState } from 'state/state.model';

export function referentialSelectorFactory<S>(stateType: Exclude<SecondaryOrderStatePath, 'cpOrder'>, selectors: S) {
  const select = (appState: AppState, tabId: string): ReferentialData | undefined =>
    appState.secondaryMarket[stateType].referential[tabId];

  return {
    getSales: (appState: AppState, tabId: string) => select(appState, tabId)?.sales,
    isSalesListEmpty: (appState: AppState, tabId: string) => {
      const sales = select(appState, tabId)?.sales;
      return isEmpty(sales);
    },

    getSettlements: (appState: AppState, tabId: string) => select(appState, tabId)?.settlements,

    getSettlementHolidays: (appState: AppState, tabId: string) => select(appState, tabId)?.settlementHolidays,

    isOtc: (appState: AppState, tabId: string) => select(appState, tabId)?.isOtc,

    getAvailableClients: (appState: AppState, tabId: string) => {
      const state = select(appState, tabId);
      return state?.isOtc === false ? state?.clients : undefined;
    },
    getAvailableOtcClients: (appState: AppState, tabId: string) => {
      const state = select(appState, tabId);
      return state?.isOtc === true ? state?.otcClients : undefined;
    },

    getIsin: (appState: AppState, tabId: string) => select(appState, tabId)?.isin,
    getRic: (appState: AppState, tabId: string) => select(appState, tabId)?.ric,

    getFileCode: (appState: AppState, tabId: string) => select(appState, tabId)?.fileCode,

    getMaturityDate: (appState: AppState, tabId: string) => select(appState, tabId)?.maturityDate,

    getQuotationType: (appState: AppState, tabId: string) => select(appState, tabId)?.quotationType,

    getPriceType: (appState: AppState, tabId: string) => select(appState, tabId)?.priceType,

    getDenomination: (appState: AppState, tabId: string) => select(appState, tabId)?.denomination,

    getCurrency: (appState: AppState, tabId: string) => select(appState, tabId)?.currency,

    getTraderGroup: (appState: AppState, tabId: string) => select(appState, tabId)?.traderGroup,

    getTraderLabel: (appState: AppState, tabId: string) => select(appState, tabId)?.traderLabel,

    getTraderMessage: (appState: AppState, tabId: string) => select(appState, tabId)?.traderMessage,

    getSalesMessage: (appState: AppState, tabId: string) => select(appState, tabId)?.salesMessage,

    getProductName: (appState: AppState, tabId: string) => select(appState, tabId)?.productName,

    getTradeAuthorization: (state: AppState, tabId: string) => select(state, tabId)?.tradeAuthorization,

    getPortfolio: (state: AppState, tabId: string) => select(state, tabId)?.portfolio,

    getAsk: (appState: AppState, tabId: string) => select(appState, tabId)?.ask,

    getBid: (appState: AppState, tabId: string) => select(appState, tabId)?.bid,
    getMaxQuantityBuy: (appState: AppState, tabId: string) => select(appState, tabId)?.sgMaxBuy,
    getMaxQuantitySell: (appState: AppState, tabId: string) => select(appState, tabId)?.sgMaxSell,
    getMinQuantity: (appState: AppState, tabId: string) => select(appState, tabId)?.sgMin,
    getLotSize: (appState: AppState, tabId: string) => select(appState, tabId)?.lotSize,
    getRemaining: (appState: AppState, tabId: string) => select(appState, tabId)?.remaining,

    getExtraBid: (appState: AppState, tabId: string) => select(appState, tabId)?.extraBid,

    getExtraAsk: (appState: AppState, tabId: string) => select(appState, tabId)?.extraAsk,

    getFirstSettlementDate: (appState: AppState, tabId: string) => select(appState, tabId)?.firstSettlementDate,

    getEligibleTraceReporting: (appState: AppState, tabId: string) =>
      select(appState, tabId)?.eligibleTraceReporting ?? false,

    getDisabledTradeWays: (tradeAuthorization?: TradeAuthorization | undefined): TradeWay[] => {
      switch (tradeAuthorization) {
        case 'Ask only':
          return ['ClientSell'];
        case 'Bid only':
          return ['ClientBuy'];
        case 'Full':
          return [];
      }
      return ['ClientBuy', 'ClientSell'];
    },

    getClientLoadingStatus: (state: AppState, tabId: string) => select(state, tabId)?.clientLoadingStatus,

    hasFailedToLoadSalesFromDomino: (state: AppState, tabId: string) =>
      select(state, tabId)?.failedToLoadSalesFromDomino,
    getInfoMessages: (state: AppState, tabId: string) => select(state, tabId)?.infos ?? [],
    getWarningMessages: (state: AppState, tabId: string) => select(state, tabId)?.warnings ?? [],
    getErrorMessages: (state: AppState, tabId: string) => select(state, tabId)?.errors ?? [],
    getSettlementLoadingStatus: (state: AppState, tabId: string) => select(state, tabId)?.settlementLoadingStatus,
    getSettlementError: (state: AppState, tabId: string) => select(state, tabId)?.settlementError,
    ...selectors,
  };
}
export type ReferentialSelector = ReturnType<typeof referentialSelectorFactory>;
